import { HttpContext } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import {
  FfdNotesInvitationDataService,
  FfdNotesInvitationDecision,
  FfdNotesInvitationPendingJoinRequest,
  NotesBaaSignRequestActionDataService,
  NotesBaaSignRequestDecision,
  NotesBaaSignRequest,
  NotesBaaSignRequestResolvedAction,
  NotesBaaSignRequestStatus,
  NotesBaaSignRequestsDataService,
  Profile,
  SiteType,
} from '@ffd/api-client';
import { ToastService, TokenService } from '@libs/shared-services';
import { authActions, selectCurrentUserProfile, selectIsUserLoggedIn, selectUserSites } from '@libs/store';
import { ffdNotesRoutePrefix, routes } from '@libs/utils';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { catchError, EMPTY, filter, forkJoin, map, Observable, of, switchMap, take, tap, timeout } from 'rxjs';
import { NOTES_BAA_SIGN_REQUEST_TOKEN } from '../../tokens/baa-sign-request.token';
import { BaaSignRequestResolveService } from '../../tokens/baa-sign-request-resolve.service';
import { FfdNotesInvitationResolveService } from '../../tokens/ffd-notes-invitation-resolve.service';

export type BaaRequestStatus = 'pending' | 'approved' | 'denied';
export type BaaRequestKind = 'baa' | 'invitation';

export interface BaaRequest {
  id: string;
  kind: BaaRequestKind;
  requesterName: string;
  requesterEmail: string;
  practiceName: string;
  status: BaaRequestStatus;
}

interface QueryParams {
  get: (key: string) => string | null;
}

@Injectable()
export class BaaRequestFlowService {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly tokenService = inject(TokenService);
  private readonly actionDataService = inject(NotesBaaSignRequestActionDataService);
  private readonly signRequestsDataService = inject(NotesBaaSignRequestsDataService);
  private readonly invitationDataService = inject(FfdNotesInvitationDataService);
  private readonly resolveService = inject(BaaSignRequestResolveService);
  private readonly invitationResolveService = inject(FfdNotesInvitationResolveService);
  private readonly toastService = inject(ToastService);

  private readonly userSites = this.store.selectSignal(selectUserSites);

  public readonly viewSignal = signal<'decision' | 'list'>('list');
  public readonly lastActionSignal = signal<'approved' | 'denied' | null>(null);
  public readonly isInitializingSignal = signal(true);
  public readonly isActionLoadingSignal = signal(false);
  public readonly signerNameSignal = signal<string | null>(null);
  public readonly baaRequestsListSignal = signal<BaaRequest[]>([]);
  public readonly activeRequestSignal = signal<BaaRequest | null>(null);

  private readonly _practiceNameSignal = signal<string | null>(null);
  public readonly practiceNameSignal = computed(
    () =>
      this._practiceNameSignal() ??
      this.activeRequestSignal()?.practiceName ??
      this.userSites().find((s) => s.siteType === SiteType.FFD_NOTES)?.name ??
      null,
  );

  public readonly otherRequestsSignal = computed(() => {
    const activeId = this.lastActionSignal() ? this.activeRequestSignal()?.id : null;
    return this.baaRequestsListSignal().filter((request) => request.id !== activeId);
  });

  public readonly notifiedRequesterNameSignal = computed(() => {
    const resolution = this.resolveService.resolutionSignal();
    const isNewPracticeCompletion =
      resolution?.action === NotesBaaSignRequestResolvedAction.NEW_PRACTICE && this.resolveService.isSignRequestCompletedSignal();
    return isNewPracticeCompletion ? resolution.requesterName : null;
  });

  public init(): void {}

  public initialize(queryParams: QueryParams): void {
    const resolution = this.resolveService.resolutionSignal();

    if (!resolution && this.invitationResolveService.resolutionSignal()) {
      this._practiceNameSignal.set(this.invitationResolveService.resolutionSignal()!.siteName);
      this.isInitializingSignal.set(false);
      this.viewSignal.set('list');
      return;
    }

    const joinRequestResolution = this.invitationResolveService.joinRequestResolutionSignal();
    if (!resolution && joinRequestResolution) {
      this.isInitializingSignal.set(true);
      this.runAuthGate$(
        this.getCurrentProfileWhenReady$().pipe(
          switchMap((profile) => this.handleInvitationAuthGate$(profile, joinRequestResolution.inviterUserId, queryParams)),
        ),
        queryParams,
      );
      return;
    }

    if (!resolution) {
      // No BAA token to resolve (e.g. direct ?joinRequestId= entry) or resolve failed
      // (e.g. 409 conflict) — guard confirmed user is logged in either way.
      // Fetch and display the full pending-approval list without email matching.
      this.isInitializingSignal.set(true);
      this.runAuthGate$(
        this.fetchPendingApproval$().pipe(switchMap((requests) => this.handlePendingApproval$(requests, queryParams))),
        queryParams,
      );
      return;
    }

    this.signerNameSignal.set(resolution.signerName);
    this._practiceNameSignal.set(resolution.siteName ?? null);

    this.runAuthGate$(
      this.getCurrentProfileWhenReady$().pipe(
        switchMap((profile) => this.handleAuthGate$(profile, resolution.signerEmail, resolution.action, queryParams)),
      ),
      queryParams,
      true,
    );
  }

  private getCurrentProfileWhenReady$(): Observable<Profile | null> {
    return this.store.select(selectIsUserLoggedIn).pipe(
      tap((isLoggedIn) => {
        if (!isLoggedIn) {
          this.store.dispatch(authActions.onProfileRequested());
        }
      }),
      filter((isLoggedIn): isLoggedIn is boolean => isLoggedIn !== null),
      take(1),
      switchMap((isLoggedIn) => (isLoggedIn ? this.store.select(selectCurrentUserProfile).pipe(take(1)) : of(null))),
    );
  }

  private runAuthGate$(result$: Observable<unknown>, queryParams: QueryParams, showToastOnError = false): void {
    result$.subscribe({
      next: (result) => this.handleDecisionResult(result, queryParams),
      error: (err) => {
        this.isInitializingSignal.set(false);
        this.isActionLoadingSignal.set(false);
        if (showToastOnError && this.viewSignal() === 'list') {
          this.toastService.error(err.message);
        }
        console.error('BaaRequestFlowService initialize error:', err);
      },
    });
  }

  public approveRequest(requestId: string): void {
    this.executeDecision(requestId, 'approved');
  }

  public denyRequest(requestId: string): void {
    this.executeDecision(requestId, 'denied');
  }

  private handleAuthGate$(
    profile: { email: string; ffdNotesBaaSigned: boolean } | null,
    signerEmail: string,
    action: NotesBaaSignRequestResolvedAction,
    queryParams: QueryParams,
  ): Observable<unknown> {
    if (profile?.email !== signerEmail) {
      if (profile) {
        this.tokenService.logout(true);
      }

      if (action === NotesBaaSignRequestResolvedAction.NEW_PRACTICE) {
        this.router.navigate([...ffdNotesRoutePrefix(), routes.baaSignupFlow], { queryParamsHandling: 'preserve' });
      } else if (action === NotesBaaSignRequestResolvedAction.ACCEPT_DENY) {
        this.redirectToLogin();
      } else {
        console.warn('BaaRequestFlowService: unexpected action type', action);
        this.redirectToLogin();
      }
      return EMPTY;
    }

    if (
      action === NotesBaaSignRequestResolvedAction.NEW_PRACTICE &&
      !profile.ffdNotesBaaSigned &&
      !this.resolveService.isSignRequestCompletedSignal()
    ) {
      this.router.navigate([...ffdNotesRoutePrefix(), routes.baaSignupFlow], { queryParamsHandling: 'preserve' });
      return EMPTY;
    }

    this.isInitializingSignal.set(true);

    if (action === NotesBaaSignRequestResolvedAction.NEW_PRACTICE && this.resolveService.isSignRequestCompletedSignal()) {
      this.store.dispatch(authActions.onProfileRequested());
      return this.store.select(selectCurrentUserProfile).pipe(
        filter((p): p is Profile => p != null && !!p.ffdNotesSiteName),
        timeout({ first: 10_000, with: () => this.store.select(selectCurrentUserProfile).pipe(take(1)) }),
        take(1),
        switchMap(() => this.fetchPendingApproval$()),
        switchMap((requests) => this.handlePendingApproval$(requests, queryParams)),
      );
    }

    return this.fetchPendingApproval$().pipe(switchMap((requests) => this.handlePendingApproval$(requests, queryParams)));
  }

  private handleInvitationAuthGate$(profile: { id: string } | null, inviterUserId: string, queryParams: QueryParams): Observable<unknown> {
    if (!profile) {
      this.redirectToLogin();
      return EMPTY;
    }

    if (profile.id !== inviterUserId) {
      return this.forceLogoutAndRedirectToLogin$();
    }

    return this.fetchPendingApproval$().pipe(switchMap((requests) => this.handlePendingApproval$(requests, queryParams)));
  }

  private forceLogoutAndRedirectToLogin$(): Observable<never> {
    const redirectUrl = this.router.url;
    this.tokenService.logout(true);
    this.router.navigate([...ffdNotesRoutePrefix(), routes.login], { queryParams: { redirectUrl } });
    return EMPTY;
  }

  private fetchPendingApproval$(): Observable<BaaRequest[]> {
    return forkJoin([
      this.signRequestsDataService.getPendingApprovalNotesBaaSignRequests().pipe(
        map(({ items }) => this.mapRequests(items)),
        catchError((err) => {
          console.error('BaaRequestFlowService: failed to fetch BAA sign requests:', err);
          return of([]);
        }),
      ),
      this.invitationDataService.getPendingApprovalFfdNotesInvitationJoinRequests().pipe(
        map(({ items }) => this.mapInvitationRequests(items)),
        catchError((err) => {
          console.error('BaaRequestFlowService: failed to fetch invitation join requests:', err);
          return of([]);
        }),
      ),
    ]).pipe(map(([baaRequests, invitationRequests]) => [...baaRequests, ...invitationRequests]));
  }

  private handlePendingApproval$(mappedRequests: BaaRequest[], queryParams: QueryParams): Observable<unknown> {
    this.baaRequestsListSignal.set(mappedRequests);

    const decision = queryParams.get('decision');
    const targetRequest = this.findTargetRequest(mappedRequests, decision, queryParams.get('joinRequestId'));
    this.activeRequestSignal.set(targetRequest);

    const willAutoSubmitDecision = !!decision && !!targetRequest;
    this.viewSignal.set(willAutoSubmitDecision ? 'decision' : 'list');

    if (!willAutoSubmitDecision) {
      this.isInitializingSignal.set(false);
    }

    if (!decision || !targetRequest) return of(null);

    this.isActionLoadingSignal.set(true);
    return this.submitAutoDecision$(targetRequest, decision);
  }

  private findTargetRequest(mappedRequests: BaaRequest[], decision: string | null, joinRequestId: string | null): BaaRequest | null {
    if (mappedRequests.length === 1) {
      return mappedRequests[0];
    }
    if (!decision) {
      return null;
    }
    if (joinRequestId) {
      return mappedRequests.find((r) => r.id === joinRequestId) ?? null;
    }

    const resolution = this.resolveService.resolutionSignal();
    if (!resolution) {
      return null;
    }
    return (
      mappedRequests.find((r) => r.requesterEmail === resolution.requesterEmail && r.practiceName === (resolution.siteName ?? '')) ?? null
    );
  }

  private submitAutoDecision$(targetRequest: BaaRequest, decision: string): Observable<unknown> {
    const isAccept = decision === 'accept';

    if (targetRequest.kind === 'invitation') {
      return this.decideInvitation$(targetRequest.id, isAccept);
    }

    const nativeDecision = isAccept ? NotesBaaSignRequestDecision.ACCEPT : NotesBaaSignRequestDecision.DECLINE;
    const token = this.resolveService.notesBaaSignRequestTokenSignal();
    return this.actionDataService.decideNotesBaaSignRequest({ decision: nativeDecision }, undefined, undefined, {
      context: new HttpContext().set(NOTES_BAA_SIGN_REQUEST_TOKEN, token),
    });
  }

  private decideInvitation$(joinRequestId: string, isAccept: boolean): Observable<unknown> {
    return this.invitationDataService.decideFfdNotesInvitationJoinRequest({
      joinRequestId,
      decision: isAccept ? FfdNotesInvitationDecision.ACCEPT : FfdNotesInvitationDecision.DECLINE,
    });
  }

  private handleDecisionResult(result: unknown, queryParams: QueryParams): void {
    this.isActionLoadingSignal.set(false);
    this.isInitializingSignal.set(false);
    if (!result || typeof result !== 'object' || !('outcome' in result)) return;

    const isAccepted = (result as { outcome: string }).outcome === 'ACCEPTED';
    this.lastActionSignal.set(isAccepted ? 'approved' : 'denied');

    if (this.viewSignal() === 'list' && queryParams.get('decision')) {
      const status: BaaRequestStatus = isAccepted ? 'approved' : 'denied';
      const activeRequestId = this.activeRequestSignal()?.id;
      this.baaRequestsListSignal.set(this.baaRequestsListSignal().map((r) => (r.id === activeRequestId ? { ...r, status } : r)));
    }
  }

  private executeDecision(requestId: string, outcome: 'approved' | 'denied'): void {
    const request = this.baaRequestsListSignal().find((r) => r.id === requestId) ?? null;
    const isFirstDecision = this.viewSignal() === 'list';
    if (isFirstDecision) {
      this.activeRequestSignal.set(request);
    }
    this.isActionLoadingSignal.set(true);

    const decision$: Observable<unknown> =
      request?.kind === 'invitation'
        ? this.decideInvitation$(requestId, outcome === 'approved')
        : this.actionDataService.decideNotesBaaSignRequest({
            decision: outcome === 'approved' ? NotesBaaSignRequestDecision.ACCEPT : NotesBaaSignRequestDecision.DECLINE,
            signRequestId: requestId,
          });

    decision$.subscribe({
      next: () => {
        this.isActionLoadingSignal.set(false);
        if (isFirstDecision) {
          this.lastActionSignal.set(outcome);
          this.viewSignal.set('decision');
        }
        this.baaRequestsListSignal.set(this.baaRequestsListSignal().map((r) => (r.id === requestId ? { ...r, status: outcome } : r)));
      },
      error: (err) => {
        this.isActionLoadingSignal.set(false);
        console.error('BaaRequestFlowService executeDecision error:', err);
      },
    });
  }

  private redirectToLogin(): void {
    this.router.navigate([...ffdNotesRoutePrefix(), routes.login], { queryParams: { redirectUrl: this.router.url } });
  }

  private mapRequests(items: NotesBaaSignRequest[]): BaaRequest[] {
    return items.map((item) => ({
      id: item.id,
      kind: 'baa',
      requesterName: item.requesterName,
      requesterEmail: item.requesterEmail,
      practiceName: item.siteName ?? '',
      status: this.mapStatus(item.status),
    }));
  }

  private mapInvitationRequests(items: FfdNotesInvitationPendingJoinRequest[]): BaaRequest[] {
    return items.map((item) => ({
      id: item.id,
      kind: 'invitation',
      requesterName: item.requesterName,
      requesterEmail: item.requesterEmail,
      practiceName: item.siteName,
      status: 'pending',
    }));
  }

  private mapStatus(status: NotesBaaSignRequestStatus): BaaRequestStatus {
    if (status === NotesBaaSignRequestStatus.COMPLETED) return 'approved';
    if (status === NotesBaaSignRequestStatus.DECLINED) return 'denied';
    return 'pending';
  }
}
